import React, { useEffect, useState } from "react";
import { X, Download } from "lucide-react";
import { Design } from "../useDesignManagement";
import { formatUSPSFormatDisplay } from "utils/formats";

interface DesignPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  design: Design | null;
  // Generates the PDF on demand when the design has no stored link (Figma exports, PAD-32394).
  onDownload?: (design: Design) => Promise<unknown>;
}

const DesignPreviewModal: React.FC<DesignPreviewModalProps> = ({ isOpen, onClose, design, onDownload }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && design) {
      setLoading(true);
      setError(null);

      // Reset loading state after a short delay to show the image
      const timer = setTimeout(() => {
        setLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isOpen, design]);

  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!design) return;

    if (onDownload) {
      setDownloading(true);
      try {
        await onDownload(design);
      } finally {
        setDownloading(false);
      }
      return;
    }

    try {
      // Use s3CmykLink if available, otherwise fall back to s3Link
      const downloadUrl = design.s3CmykLink || design.s3Link;

      if (!downloadUrl) {
        console.warn("No download link available for design");
        setError("No download link available for this design");
        return;
      }

      // Simply redirect to the URL instead of handling download internally
      window.open(downloadUrl, "_blank");
    } catch (error) {
      console.error("Error downloading design:", error);
      setError("Failed to download design");
    }
  };

  if (!isOpen || !design) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 bottom-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      style={{ margin: 0, padding: 0 }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-7xl max-h-[95vh] w-full mx-4 flex flex-col relative z-[10000]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex flex-col">
            <h2 className="text-xl font-bold text-gray-900">{design.designName || "Design Preview"}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Format: {formatUSPSFormatDisplay(design.format)} • Designer: {design.designer}
            </p>
          </div>
          <button
            data-cy="designPreviewModalCloseButton"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            aria-label="Close preview"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center h-96 text-gray-500">
              <p className="text-2xl mb-4">⚠️</p>
              <p className="text-lg">{error}</p>
            </div>
          )}

          {!loading && !error && design.imageSrc && (
            <div className="w-full h-full flex items-center justify-center min-h-[600px]">
              <img
                src={design.imageSrc}
                alt={design.designName}
                className="max-w-none max-h-none w-auto h-auto object-contain rounded-lg shadow-lg"
                style={{ maxWidth: "90%", maxHeight: "80vh" }}
                onLoad={() => setLoading(false)}
                onError={() => {
                  setLoading(false);
                  setError("Failed to load design preview");
                }}
              />
            </div>
          )}

          {!loading && !error && !design.imageSrc && (
            <div className="flex flex-col items-center justify-center h-96 text-gray-500">
              <p className="text-2xl mb-4">📄</p>
              <p className="text-lg">No preview available for this design</p>
            </div>
          )}
        </div>

        {/* Footer with Actions */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            {design.dateCreated && !isNaN(design.dateCreated.getTime()) && (
              <span>Created: {design.dateCreated.toLocaleDateString()}</span>
            )}
            {design.updatedAt && !isNaN(new Date(design.updatedAt).getTime()) && (
              <span>• Updated: {new Date(design.updatedAt).toLocaleDateString()}</span>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              data-cy="designPreviewModalDownloadButton"
              data-cypress-id="design-preview-modal-download-button"
              onClick={handleDownload}
              disabled={downloading}
              aria-busy={downloading}
              className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              <span>{downloading ? "Preparing…" : "Download"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignPreviewModal;
